import { useLanguage } from "@context/use-language";
import { useAuthUser } from "@data/auth";
import { ButtonProps, HeaderNavMenuItem } from "@dm-4dev/ui";
import { useGroupStore } from "@mod/groups";
import clsx from "clsx";
import {
  BookOpenIcon,
  ChurchIcon,
  HeartHandshakeIcon,
  HomeIcon,
  LockKeyholeIcon,
  MailIcon,
  MessageSquareIcon,
  UserCircle2Icon,
  Users2Icon
} from "lucide-react";
import { useLinkClickHandler, useNavigate } from "react-router-dom";
import { APP_ROUTES } from "../../router";

export const useMainMenu = () => {
  const currentPath = window.location.pathname;
  const {
    hasAuthRole,
    hasViewServicesAccess,
    hasViewInvitationsAccess,
    hasViewPrayerRequestsAccess,
    hasViewCommitmentsAccess,
    hasViewPreachingAccess,
    hasViewEventsAccess,
    hasViewPreachingAssignmentsAccess,
    hasLandingAdminAccess,
    hasEditMeetingAttendanceSecondaryAccess,
    userData
  } = useAuthUser();
  const linkToEvent = useLinkClickHandler(APP_ROUTES.EVENTS);
  const linkToSecurity = useLinkClickHandler(APP_ROUTES.SECURITY);
  const linkToServices = useLinkClickHandler(APP_ROUTES.SERVICES);
  const linkToInvitations = useLinkClickHandler(APP_ROUTES.INVITATIONS);
  const linkToCommitments = useLinkClickHandler(APP_ROUTES.COMMITMENT);
  const linkToPrayerRequests = useLinkClickHandler(APP_ROUTES.PRAYER_REQUESTS);
  const linkToPreaching = useLinkClickHandler(APP_ROUTES.PREACHING);
  const linkToProfile = useLinkClickHandler(APP_ROUTES.PROFILE);
  const { setViewAllGroups } = useGroupStore();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const navButtonProps = (selected?: boolean): ButtonProps => ({
    align: "left",
    className: clsx(
      "w-full justify-start font-semibold hover:bg-primary/5 text-slate-700 hover:text-primary active:text-primary !px-4 !py-3 !my-1 rounded-lg",
      {
        "!bg-primary/10 !text-primary": selected
      }
    ),
    variant: "text"
  });

  const mainNavMenu: (HeaderNavMenuItem | null)[] = [
    {
      label: t("mainMenuHome"),
      path: APP_ROUTES.HOME,
      buttonProps: {
        ...navButtonProps(currentPath === APP_ROUTES.HOME),
        onClick: () => {
          navigate(APP_ROUTES.HOME);
          // if (userData?.groups.length) setViewAllGroups(true);
        },
        startIcon: <HomeIcon size={20} />
      }
    },
    hasViewEventsAccess
      ? {
          label: t("mainMenuEvents"),
          buttonProps: {
            ...navButtonProps(currentPath === APP_ROUTES.EVENTS),
            onClick: (e: any) => {
              linkToEvent(e);
              setViewAllGroups(false);
            },
            startIcon: <ChurchIcon size={20} />
          }
        }
      : null,
    hasViewCommitmentsAccess
      ? {
          label: t("mainMenuCommitments"),
          buttonProps: {
            ...navButtonProps(currentPath === APP_ROUTES.COMMITMENT),
            onClick: (e: any) => {
              linkToCommitments(e);
              if (userData?.groups.length) setViewAllGroups(true);
            },
            startIcon: <HeartHandshakeIcon size={20} />
          }
        }
      : null,
    hasViewServicesAccess || hasEditMeetingAttendanceSecondaryAccess
      ? {
          label: t("mainMenuServices"),
          buttonProps: {
            ...navButtonProps(currentPath === APP_ROUTES.SERVICES),
            onClick: (e: any) => {
              linkToServices(e);
              setViewAllGroups(false);
            },
            startIcon: <Users2Icon size={20} />
          }
        }
      : null,
    hasViewPreachingAccess || hasViewPreachingAssignmentsAccess
      ? {
          label: t("mainMenuPreaching"),
          buttonProps: {
            ...navButtonProps(currentPath === APP_ROUTES.PREACHING),
            onClick: (e: any) => {
              linkToPreaching(e);
            },
            startIcon: <BookOpenIcon size={20} />
          }
        }
      : null,
    hasViewInvitationsAccess
      ? {
          label: t("mainMenuInvitations"),
          buttonProps: {
            ...navButtonProps(currentPath === APP_ROUTES.INVITATIONS),
            onClick: (e: any) => {
              linkToInvitations(e);
            },
            startIcon: <MailIcon size={20} />
          }
        }
      : null,
    hasAuthRole
      ? {
          label: t("mainMenuSecurity"),
          buttonProps: {
            ...navButtonProps(currentPath === APP_ROUTES.SECURITY),
            onClick: (e: any) => {
              linkToSecurity(e);
            },
            startIcon: <LockKeyholeIcon size={20} />
          }
        }
      : null,
    hasViewPrayerRequestsAccess
      ? {
          label: t("mainMenuPrayerRequests"),
          buttonProps: {
            ...navButtonProps(currentPath.includes(APP_ROUTES.PRAYER_REQUESTS)),
            onClick: (e: any) => {
              linkToPrayerRequests(e);
            },
            startIcon: <MessageSquareIcon size={20} />
          }
        }
      : null,
    hasLandingAdminAccess
      ? {
          label: "Landing Config",
          buttonProps: {
            ...navButtonProps(currentPath.includes(APP_ROUTES.LANDING_CONFIG)),
            onClick: () => navigate(APP_ROUTES.LANDING_CONFIG),
            startIcon: <BookOpenIcon size={20} />
          }
        }
      : null,
    {
      label: t("myProfile"),
      buttonProps: {
        ...navButtonProps(currentPath === APP_ROUTES.PROFILE),
        onClick: (e: any) => {
          linkToProfile(e);
        },
        startIcon: <UserCircle2Icon size={20} />
      }
    }
  ];

  return mainNavMenu.filter((o) => !!o);
};
